/**
 * InvitePublicLandingPage — Phase 5.
 *
 * Public landing for `/invite/:token` when the user is NOT yet authenticated.
 * Mirrors the Signup two-pane layout (PixelBlast brand side + form side) and
 * reuses the same PasswordInput strength + requirements UI. The only thing
 * that changes vs Signup is the heading copy and the form fields — the
 * invitee already has an email + role, so we ask for name + password.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { Checkbox } from '@/components/ui/checkbox';
import artistIntroLogoWhite from '../assets/artist_intro_white.svg';
import PixelBlast from '../components/PixelBlast';
import {
  lookupInvitationPublic,
  acceptInvitationWithSignup,
  type InvitationPublicPreview,
} from '../services/organizationApi';
import { LEGAL_URLS } from '@/lib/consent';
import { useConsent } from '@/context/ConsentContext';

interface User {
  _id: string;
  username: string;
  user_type: string;
  email: string;
  domain: string;
}

interface Props {
  onLogin: (token: string, user: User, rememberMe?: boolean) => void;
}

// Maps a wire-side role value (`owner`/`admin`/`manager`) to the friendly
// label used everywhere in the management-company UI. The DB still stores
// the raw role; this is presentation-only.
const roleLabel = (role: string) => {
  switch ((role || '').toLowerCase()) {
    case 'owner': return 'Owner';
    case 'admin': return 'Company Admin';
    case 'manager': return 'Artist Representative';
    default:
      if (!role) return 'team member';
      return role.charAt(0).toUpperCase() + role.slice(1);
  }
};

const InvitePublicLandingPage: React.FC<Props> = ({ onLogin }) => {
  const consent = useConsent();
  const params = useParams<{ token: string }>();
  // The unauthenticated entry in App.tsx renders this OUTSIDE a <Route>, so
  // useParams returns {}. Fall back to parsing the pathname directly.
  const token = useMemo(() => {
    if (params.token) return params.token;
    const m = window.location.pathname.match(/^\/invite\/([^/?#]+)/);
    return m ? m[1] : undefined;
  }, [params.token]);
  const navigate = useNavigate();

  const [previewState, setPreviewState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [previewError, setPreviewError] = useState('');
  const [preview, setPreview] = useState<InvitationPublicPreview | null>(null);

  const [fullName, setFullName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [legalConsent, setLegalConsent] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const ranRef = useRef(false);
  const passwordInputRef = useRef<HTMLInputElement>(null);

  // Lock body scroll like Signup does.
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;
    if (!token) {
      setPreviewState('error');
      setPreviewError('This invite link is incomplete. Ask the sender to share it again.');
      return;
    }
    (async () => {
      try {
        const data = await lookupInvitationPublic(token);
        setPreview(data);
        setPreviewState('ready');
      } catch (err: any) {
        setPreviewState('error');
        setPreviewError(
          err?.response?.data?.message || 'We couldn’t load this invitation.',
        );
      }
    })();
  }, [token]);

  const goToLoginAndResume = () => {
    if (!token) return;
    try { sessionStorage.setItem('pendingInviteToken', token); } catch {}
    navigate('/login', { replace: true });
  };

  const handleCreateAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError('');
    if (!token || !preview) return;
    if (!fullName.trim()) { setSubmitError('Please enter your full name.'); return; }
    if (password.length < 8) { setSubmitError('Password must be at least 8 characters.'); return; }
    if (password !== confirmPassword) { setSubmitError('Passwords don’t match.'); return; }
    if (!legalConsent) { setSubmitError('Please agree to the Terms of Service and Privacy Policy.'); return; }

    setSubmitting(true);
    try {
      const result = await acceptInvitationWithSignup({
        token,
        fullName: fullName.trim(),
        password,
      });
      // /accept-with-signup atomically creates the account AND accepts the
      // invite. Clear the pending-token stash so App.tsx's post-login
      // resume doesn't re-route to the authed accept page (which would
      // 409 with "Invitation is accepted").
      try { sessionStorage.removeItem('pendingInviteToken'); } catch {}
      toast.success('Welcome to Artist Intro');
      // onLogin stores the token first so axiosInstance can attach it.
      // Then flip ConsentContext to authenticated and record the invite-time
      // acceptance so the audit-log row is attributed to the new userId.
      onLogin(result.token, result.user as unknown as User, true);
      consent.setAuthenticated(true);
      consent.acknowledge('invite');
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Could not accept invitation.';
      const accountExists = err?.response?.data?.hasAccount === true;
      setSubmitError(msg);
      if (accountExists) {
        toast.message('Please sign in to accept this invitation.');
        goToLoginAndResume();
      }
    } finally {
      setSubmitting(false);
    }
  };

  const blocked =
    preview &&
    (preview.status !== 'pending' || !preview.organizationUsable);

  // Brand panel copy reacts to context.
  const isDirect = !!(preview as any)?.direct;
  const targetLabel = preview?.organizationName || 'a team';

  // ── Branded side (always rendered so layout is stable across states) ──
  const brandSide = (
    <div className="hidden lg:flex flex-col justify-between bg-primary pt-20 pb-20 p-16 text-white overflow-hidden relative">
      <PixelBlast
        className="absolute! inset-0"
        color="#3b82f6"
        variant="circle"
        pixelSize={4}
        speed={0.3}
        patternDensity={1.8}
        edgeFade={0}
        enableRipples
        rippleSpeed={0.25}
        rippleThickness={0.12}
      />
      <div className="relative z-10">
        <img src={artistIntroLogoWhite} alt="Artist Intro" className="h-14" />
      </div>
      <div className="relative z-10">
        <h1 className="text-3xl sm:text-4xl font-bold mb-4">
          {previewState === 'ready' && !blocked ? 'Join the team' : 'You’re invited'}
        </h1>
        <p className="text-base sm:text-lg text-white/80 max-w-md">
          {previewState === 'loading'
            ? 'Hang on while we fetch your invitation details…'
            : previewState === 'error'
              ? 'We couldn’t load this invitation. Ask the sender to share a fresh link.'
              : blocked
                ? 'This invitation can no longer be used. Reach out to the sender for a new one.'
                : isDirect
                  ? `Manage ${targetLabel} on Artist Intro. Sign in with your own account — your access is scoped only to this artist.`
                  : `You’ve been invited to collaborate on ${targetLabel}. Set up your account in seconds and start helping out.`}
        </p>
      </div>
    </div>
  );

  // ── Mobile branding strip (matches Signup) ──
  const mobileBrand = (
    <div className="lg:hidden bg-primary px-6 py-5 shrink-0">
      <img src={artistIntroLogoWhite} alt="Artist Intro" className="h-7" />
    </div>
  );

  // ── Form-side wrapper. Children render the appropriate state. ──
  const formSide = (children: React.ReactNode) => (
    <div className="relative flex flex-col h-dvh bg-white">
      {mobileBrand}
      <div className="flex-1 min-h-0 flex flex-col p-6 sm:p-12 lg:px-20 lg:pt-20 lg:pb-12">
        <div className="w-full max-w-md mx-auto flex flex-col flex-1 min-h-0">
          {children}
        </div>
      </div>
    </div>
  );

  // ── Loading state ──
  if (previewState === 'loading') {
    return (
      <div className="min-h-screen lg:grid lg:grid-cols-2 bg-white">
        {brandSide}
        {formSide(
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center space-y-3">
              <Spinner size="xl" className="mx-auto" />
              <p className="text-sm text-muted-foreground">Loading your invitation…</p>
            </div>
          </div>,
        )}
      </div>
    );
  }

  // ── Error state ──
  if (previewState === 'error' || !preview) {
    return (
      <div className="min-h-screen lg:grid lg:grid-cols-2 bg-white">
        {brandSide}
        {formSide(
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center space-y-4 max-w-sm">
              <AlertCircle className="w-10 h-10 text-destructive mx-auto" />
              <h2 className="text-2xl font-bold text-foreground">Invitation unavailable</h2>
              <p className="text-sm text-muted-foreground">{previewError}</p>
              <Button variant="outline" onClick={() => navigate('/login', { replace: true })}>
                Go to sign in
              </Button>
            </div>
          </div>,
        )}
      </div>
    );
  }

  // ── Blocked state (expired / accepted / revoked / org dead) ──
  if (blocked) {
    return (
      <div className="min-h-screen lg:grid lg:grid-cols-2 bg-white">
        {brandSide}
        {formSide(
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center space-y-4 max-w-sm">
              <AlertCircle className="w-10 h-10 text-destructive mx-auto" />
              <h2 className="text-2xl font-bold text-foreground">
                {preview.status === 'expired' ? 'Invitation expired' : 'Invitation unavailable'}
              </h2>
              <p className="text-sm text-muted-foreground">
                {preview.status === 'expired'
                  ? 'Ask the sender to send you a fresh invitation link.'
                  : preview.status === 'accepted'
                    ? 'This invitation has already been accepted.'
                    : preview.status === 'revoked'
                      ? 'This invitation was revoked by the sender.'
                      : 'This organization is no longer active.'}
              </p>
              <Button variant="outline" onClick={() => navigate('/login', { replace: true })}>
                Go to sign in
              </Button>
            </div>
          </div>,
        )}
      </div>
    );
  }

  // ── Already-has-account branch ──
  if (preview.hasAccount) {
    return (
      <div className="min-h-screen lg:grid lg:grid-cols-2 bg-white">
        {brandSide}
        {formSide(
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center space-y-5 max-w-sm">
              <h2 className="text-2xl sm:text-3xl font-bold text-foreground">
                Welcome back
              </h2>
              <p className="text-sm text-muted-foreground">
                An account already exists for{' '}
                <span className="text-primary font-medium">{preview.email}</span>.
                Sign in to accept your invitation to{' '}
                <span className="text-primary font-medium">{preview.organizationName || 'this team'}</span>.
              </p>
              <Button size="lg-full" onClick={goToLoginAndResume}>
                Sign in to accept
              </Button>
            </div>
          </div>,
        )}
      </div>
    );
  }

  // ── Signup form (mirrors Signup step 3 layout) ──
  return (
    <div className="min-h-screen lg:grid lg:grid-cols-2 bg-white">
      {brandSide}
      {formSide(
        <>
          {/* Fixed top: heading */}
          <div className="shrink-0 space-y-2">
            <h2 className="text-2xl sm:text-3xl font-bold text-foreground text-center">
              Join <span className="text-primary">{preview.organizationName || 'the team'}</span>
            </h2>
            <p className="mt-2 text-sm text-muted-foreground text-center">
              as <span className="font-medium text-foreground">{roleLabel(preview.invitedRole)}</span>
              {' · '}
              <span className="text-primary">{preview.email}</span>
            </p>
          </div>

          {/* Scrollable middle */}
          <div
            className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden mt-6 scrollbar-hide px-1 -mx-1"
            style={{ scrollbarWidth: 'none' }}
          >
            <form onSubmit={handleCreateAccount} className="space-y-6">
              <div className="space-y-5">
                <div>
                  <label htmlFor="invite-fullname" className="block text-sm font-semibold text-foreground mb-2">
                    Your full name
                  </label>
                  <Input
                    id="invite-fullname"
                    type="text"
                    required
                    value={fullName}
                    onChange={(e) => { setFullName(e.target.value.replace(/^\s+/, '')); setSubmitError(''); }}
                    placeholder="Your name"
                  />
                </div>

                <div>
                  <label htmlFor="invite-password" className="block text-sm font-semibold text-foreground mb-2">
                    Password
                  </label>
                  <PasswordInput
                    id="invite-password"
                    required
                    minLength={8}
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setSubmitError(''); }}
                    placeholder="Minimum 8 characters"
                    ref={passwordInputRef}
                    showStrengthMeter
                    showCopyBtn
                    showRequirements
                  />
                </div>

                <div>
                  <label htmlFor="invite-password-confirm" className="block text-sm font-semibold text-foreground mb-2">
                    Confirm Password
                  </label>
                  <PasswordInput
                    id="invite-password-confirm"
                    required
                    minLength={6}
                    value={confirmPassword}
                    onChange={(e) => { setConfirmPassword(e.target.value); setSubmitError(''); }}
                    placeholder="Re-enter your password"
                  />
                </div>

                {/* Mandatory Terms + Privacy acceptance */}
                <div className="flex items-start gap-2.5">
                  <Checkbox
                    id="invite-legal-consent"
                    checked={legalConsent}
                    onCheckedChange={(checked) => setLegalConsent(checked === true)}
                    className="mt-0.5"
                  />
                  <label htmlFor="invite-legal-consent" className="text-sm font-medium text-foreground cursor-pointer leading-snug">
                    I agree to the{' '}
                    <a href={LEGAL_URLS.terms} target="_blank" rel="noopener noreferrer" className="text-primary underline hover:text-primary/80">
                      Terms of Service
                    </a>{' '}
                    and{' '}
                    <a href={LEGAL_URLS.privacy} target="_blank" rel="noopener noreferrer" className="text-primary underline hover:text-primary/80">
                      Privacy Policy
                    </a>
                    .
                  </label>
                </div>
              </div>

              {submitError && (
                <div className="text-sm text-danger bg-danger-light border border-danger/20 rounded-md p-4">
                  {submitError}
                </div>
              )}

              <Button
                type="submit"
                size="lg-full"
                disabled={submitting || !legalConsent}
                loading={submitting}
                loadingText="Creating account…"
              >
                Create account &amp; join
              </Button>
            </form>

            <p className="text-center text-sm text-muted-foreground py-4">
              Already have an account?{' '}
              <button
                type="button"
                onClick={goToLoginAndResume}
                className="font-semibold text-primary hover:text-primary/80 transition-colors"
              >
                Log in
              </button>
            </p>
          </div>

          {/* Fixed bottom footer */}
          <div className="shrink-0 pt-4 flex justify-center gap-3 sm:gap-6 text-[10px] sm:text-xs tracking-wider text-muted-foreground">
            <a href={LEGAL_URLS.terms} target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors uppercase">Terms of Service</a>
            <a href={LEGAL_URLS.privacy} target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors uppercase">Privacy Policy</a>
            <a href={LEGAL_URLS.cookies} target="_blank" rel="noopener noreferrer" className="hover:text-foreground transition-colors uppercase">Cookie Policy</a>
          </div>
        </>,
      )}
    </div>
  );
};

export default InvitePublicLandingPage;
